import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { EventMessage, EventType, InteractionType, PublicClientApplication, Configuration, LogLevel, AccountInfo } from '@azure/msal-browser';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ScienceIcon from '@mui/icons-material/Science';

interface MsalTraceViewerProps {
  onBack?: () => void;
}

interface TraceItem {
  ts: number;
  type: string;
  eventType: EventType;
  correlationId?: string;
  interactionType?: InteractionType;
  message?: string;
  payload?: any;
  error?: string;
  source?: 'main' | 'test'; // Track which MSAL instance generated this event
}

function redact(obj: any, enablePII: boolean) {
  if (!obj) return obj;
  
  // Always handle circular references and non-serializable objects
  const seen = new WeakSet();
  
  try {
    const s = JSON.stringify(obj, (k, v) => {
      // Skip functions
      if (typeof v === 'function') {
        return '[Function]';
      }
      
      // Handle DOM elements and Window objects
      if (v && typeof v === 'object') {
        if (v instanceof Window) {
          return '[Window]';
        }
        if (v instanceof HTMLElement) {
          return `[HTMLElement: ${v.tagName}]`;
        }
        if (v instanceof Node) {
          return '[DOM Node]';
        }
        
        // Handle circular references
        if (seen.has(v)) {
          return '[Circular Reference]';
        }
        seen.add(v);
      }
      
      // Only redact PII if enablePII is false
      if (!enablePII) {
        const key = String(k).toLowerCase();
        if (key.includes('token') || key.includes('secret') || key === 'idtoken' || key === 'accesstoken') {
          return '[redacted]';
        }
        if (key === 'username' || key === 'email' || key === 'login_hint') {
          return '[redacted]';
        }
      }
      
      return v;
    });
    return JSON.parse(s);
  } catch (err) {
    // If stringify still fails, try to extract basic info
    if (obj && typeof obj === 'object') {
      try {
        return {
          __note: 'Non-serializable object',
          __keys: Object.keys(obj).slice(0, 10), // Show first 10 keys
          __prototype: obj.constructor?.name || 'unknown'
        };
      } catch {
        return { __error: 'Could not serialize object', __type: typeof obj };
      }
    }
    return { __error: 'Could not serialize object', __type: typeof obj };
  }
}

const MsalTraceViewer: React.FC<MsalTraceViewerProps> = ({ onBack }) => {
  const { instance, accounts } = useMsal();
  // Always target the app's active account for tests to avoid unintended user switches
  const targetAccount = useMemo(() => instance.getActiveAccount() || accounts[0] || null, [instance, accounts]);
  const [logs, setLogs] = useState<TraceItem[]>([]);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [enablePII, setEnablePII] = useState(false);
  const [copyOk, setCopyOk] = useState(false);
  const [filterInteraction, setFilterInteraction] = useState<InteractionType | 'all'>('all');
  const [testLoading, setTestLoading] = useState(false);
  
  // NEW: Test mode and isolated MSAL instance
  const [useTestMode, setUseTestMode] = useState(false);
  const [testInstance, setTestInstance] = useState<PublicClientApplication | null>(null);
  const [testAccount, setTestAccount] = useState<AccountInfo | null>(null);

  // Attach MSAL event callback to main instance
  useEffect(() => {
    if (!instance) return;
    const id = instance.addEventCallback((e: EventMessage) => {
      const correlationId = (e as any).correlationId || (e.payload as any)?.correlationId || undefined;
      const interaction = (e as any).interactionType ?? undefined;
      const item: TraceItem = {
        ts: Date.now(),
        type: 'event',
        eventType: e.eventType as EventType,
        correlationId,
        interactionType: interaction as InteractionType | undefined,
        payload: e.payload,
        error: (e as any).error ? String((e as any).error?.message || (e as any).error) : undefined,
        source: 'main', // Tag as main app instance
      };
      setLogs(prev => [...prev, item]);
    });
    return () => { if (id) instance.removeEventCallback(id); };
  }, [instance]);

  // NEW: Create and attach event callback to test instance when test mode is enabled
  useEffect(() => {
    if (!useTestMode || testInstance) return;
    
    // Get the main app's client ID from the existing instance using the public API
    const mainClientId = instance.getConfiguration().auth.clientId;
    
    const testConfig: Configuration = {
      auth: {
        clientId: mainClientId,
        authority: 'https://login.microsoftonline.com/common',
        redirectUri: window.location.origin,
      },
      cache: {
        // CRITICAL: Use localStorage to isolate from main app's sessionStorage
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message) => {
            if (message.includes('block_nested_popups')) return;
            console.log(`[TestMSAL][${LogLevel[level]}]`, message);
          },
        },
      },
    };

    const testMsal = new PublicClientApplication(testConfig);
    testMsal
      .initialize()
      .then(() => {
        console.log('✅ Test MSAL instance initialized with localStorage');
        setTestInstance(testMsal);
        
        // Attach event callback
        const id = testMsal.addEventCallback((e: EventMessage) => {
          const correlationId = (e as any).correlationId || (e.payload as any)?.correlationId || undefined;
          const interaction = (e as any).interactionType ?? undefined;
          const item: TraceItem = {
            ts: Date.now(),
            type: 'event',
            eventType: e.eventType as EventType,
            correlationId,
            interactionType: interaction as InteractionType | undefined,
            payload: e.payload,
            error: (e as any).error ? String((e as any).error?.message || (e as any).error) : undefined,
            source: 'test', // Tag as test instance
          };
          setLogs(prev => [...prev, item]);
        });
      })
      .catch((err) => {
        console.error('❌ Test MSAL initialization failed:', err);
      });

    return () => {
      // Cleanup is handled when switching modes
    };
  }, [useTestMode, testInstance, instance]);

  const groupedByCorrelation = useMemo(() => {
    const groups: Record<string, TraceItem[]> = {};
    for (const l of logs) {
      const key = l.correlationId || 'no-correlation';
      if (!groups[key]) groups[key] = [];
      groups[key].push(l);
    }
    return groups;
  }, [logs]);

  const filteredGroups = useMemo(() => {
    const out: Array<[string, TraceItem[]]> = [];
    Object.entries(groupedByCorrelation).forEach(([cid, items]) => {
      const filtered = items.filter(it => {
        if (errorsOnly && !it.error) return false;
        if (filterInteraction !== 'all' && it.interactionType && it.interactionType !== filterInteraction) return false;
        return true;
      });
      if (filtered.length) out.push([cid, filtered]);
    });
    // Sort by first timestamp per group
    out.sort((a, b) => (a[1][0]?.ts || 0) - (b[1][0]?.ts || 0));
    return out;
  }, [groupedByCorrelation, errorsOnly, filterInteraction]);

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setCopyOk(true); setTimeout(()=>setCopyOk(false), 1200); } catch {}
  };

  const exportJson = () => {
    const data = JSON.stringify(logs.map(l => ({ ...l, payload: redact(l.payload, enablePII) })), null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `msal-trace-${new Date().toISOString().replace(/[:.]/g,'-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Test actions to generate MSAL events
  const testSilentToken = async () => {
    const activeInstance = useTestMode ? testInstance : instance;
    const activeAccount = useTestMode ? testAccount : accounts[0];
    
    if (!activeAccount) {
      alert(useTestMode ? 'Please sign in to test instance first' : 'Please sign in first');
      return;
    }
    setTestLoading(true);
    try {
      await activeInstance!.acquireTokenSilent({
        scopes: ['User.Read'],
        account: activeAccount,
      });
    } catch (err: any) {
      console.log('Silent token test completed (may have errors):', err);
    } finally {
      setTestLoading(false);
    }
  };

  // Safe negative test: request an obviously invalid scope to produce a predictable failure
  const testSilentTokenExpectedFailure = async () => {
    const activeInstance = useTestMode ? testInstance : instance;
    const activeAccount = useTestMode ? testAccount : accounts[0];
    
    if (!activeAccount) {
      alert(useTestMode ? 'Please sign in to test instance first' : 'Please sign in first');
      return;
    }
    setTestLoading(true);
    try {
      await activeInstance!.acquireTokenSilent({
        scopes: ['this.scope.does.not.exist'],
        account: activeAccount,
        // Ensure we don't satisfy from cache if something odd exists
        forceRefresh: true,
      } as any);
    } catch (err: any) {
      console.log('Silent token (expected failure) completed:', err?.message || err);
    } finally {
      setTestLoading(false);
    }
  };

  const testPopupToken = async () => {
    const activeInstance = useTestMode ? testInstance : instance;
    const activeAccount = useTestMode ? testAccount : targetAccount;
    
    if (!activeAccount) {
      alert(useTestMode ? 'Please sign in to test instance first' : 'Please sign in first');
      return;
    }
    setTestLoading(true);
    try {
      await activeInstance!.acquireTokenPopup({
        scopes: ['Mail.Read'],
        account: activeAccount,
      });
    } catch (err: any) {
      console.log('Popup token test completed (may have been cancelled):', err);
    } finally {
      setTestLoading(false);
    }
  };

  // NEW: Login to test instance
  const testLogin = async () => {
    if (!testInstance) {
      alert('Test instance not initialized');
      return;
    }
    setTestLoading(true);
    try {
      const result = await testInstance.loginPopup({
        scopes: ['openid', 'profile', 'User.Read'],
        prompt: 'select_account',
      });
      setTestAccount(result.account);
      console.log('✅ Test instance login successful:', result.account?.username);
    } catch (err: any) {
      console.log('Test login completed (may have been cancelled):', err);
    } finally {
      setTestLoading(false);
    }
  };

  // NEW: Logout from test instance (safe - won't affect main app)
  const testLogout = async () => {
    if (!testInstance || !testAccount) {
      alert('No test account to sign out');
      return;
    }
    setTestLoading(true);
    try {
      await testInstance.logoutPopup({ account: testAccount });
      setTestAccount(null);
      console.log('✅ Test instance logout successful');
    } catch (err: any) {
      console.error('Test logout error:', err);
    } finally {
      setTestLoading(false);
    }
  };


  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          {onBack && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                mr: 2,
                borderRadius: 2,
                pl: 1.5,
                color: "text.secondary",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderColor: "primary.main",
                  color: "primary.main"
                }
              }}
            >
              Back
            </Button>
          )}

          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              MSAL Trace Viewer
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Live view of msal-browser events grouped by correlationId
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Controls */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <Accordion defaultExpanded sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterAltIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Filters & Controls</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3} alignItems="center">
              {/* Stats */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {filteredGroups.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Correlation Groups
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {logs.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Events
                  </Typography>
                </Paper>
              </Grid>

              {/* Toggles */}
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={useTestMode} 
                        onChange={(e)=> setUseTestMode(e.target.checked)}
                        color="secondary"
                      />
                    } 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ScienceIcon sx={{ fontSize: '1rem' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Test Mode (Isolated Instance)
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={errorsOnly} 
                        onChange={(e)=> setErrorsOnly(e.target.checked)} 
                        color="error"
                      />
                    } 
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Show errors only
                      </Typography>
                    }
                  />
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={enablePII} 
                        onChange={(e)=> setEnablePII(e.target.checked)}
                        color="warning"
                      />
                    } 
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Allow PII in payloads
                      </Typography>
                    }
                  />
                </Stack>
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="error" 
                    startIcon={<DeleteSweepIcon />} 
                    onClick={()=> setLogs([])}
                    sx={{ borderRadius: 2 }}
                  >
                    Clear All Events
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    startIcon={<DownloadIcon />} 
                    onClick={exportJson}
                    sx={{ borderRadius: 2 }}
                  >
                    Export to JSON
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Card>

      {/* Test Actions */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>

          <Box sx={{ display: 'flex', alignItems: 'center', pb: 1, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TimelineIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Test Actions</Typography>
          </Box>

          {useTestMode && testInstance && (
            <Alert severity="success" sx={{ mb: 2, fontSize: '0.875rem' }}>
              <strong>🧪 Test Mode Active:</strong> Actions use isolated MSAL instance with localStorage.
              {testAccount ? (
                <> Signed in as: <strong>{testAccount.username}</strong></>
              ) : (
                <> Click "Test Login" to authenticate with isolated instance.</>  
              )}
            </Alert>
          )}

          {!useTestMode && (
            <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
              <strong>Main App Mode:</strong> Monitoring main application MSAL instance.
              {accounts.length > 0 ? (
                <> Signed in as: <strong>{accounts[0].username}</strong></>
              ) : (
                <> Please sign in to enable tests.</>  
              )}
            </Alert>
          )}

          <Grid container spacing={2}>
            {useTestMode && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Button 
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={testLogin}
                    disabled={testLoading || !testInstance || !!testAccount}
                    sx={{ 
                      height: 56,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Test Login<br />
                    <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.9 }}>(Isolated)</Typography>
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button 
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<LogoutIcon />}
                    onClick={testLogout}
                    disabled={testLoading || !testAccount}
                    sx={{ 
                      height: 56,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Test Logout<br />
                    <Typography variant="caption" color="text.secondary">(Safe)</Typography>
                  </Button>
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <Button 
                fullWidth
                variant="outlined" 
                onClick={testSilentToken}
                disabled={testLoading || (useTestMode ? !testAccount : !accounts.length)}
                sx={{ 
                  height: 56,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Silent Token<br />
                <Typography variant="caption" color="text.secondary">(User.Read)</Typography>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button 
                fullWidth
                variant="outlined" 
                onClick={testSilentTokenExpectedFailure}
                disabled={testLoading || (useTestMode ? !testAccount : !accounts.length)}
                sx={{ 
                  height: 56,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Silent Token<br />
                <Typography variant="caption" color="text.secondary">(Expected failure)</Typography>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button 
                fullWidth
                variant="outlined" 
                onClick={testPopupToken}
                disabled={testLoading || (useTestMode ? !testAccount : !accounts.length)}
                sx={{ 
                  height: 56,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Popup Token<br />
                <Typography variant="caption" color="text.secondary">(Mail.Read)</Typography>
              </Button>
            </Grid>
          </Grid>

          {testLoading && (
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mt: 2, 
                bgcolor: '#e3f2fd', 
                borderColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="Running..." size="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Operation in progress...
                </Typography>
              </Box>
              <Button 
                size="small" 
                variant="text" 
                color="error"
                onClick={() => setTestLoading(false)}
                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
              >
                Force Reset
              </Button>
            </Paper>
          )}

          {!useTestMode && !accounts.length && (
            <Alert severity="warning" sx={{ mt: 2, fontSize: '0.875rem' }}>
              💡 Sign in to main app first to enable tests
            </Alert>
          )}
          
          {useTestMode && !testInstance && (
            <Alert severity="info" sx={{ mt: 2, fontSize: '0.875rem' }}>
              ⏳ Initializing isolated test instance...
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Event Display */}
      {filteredGroups.length === 0 ? (
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 6, 
            textAlign: 'center', 
            borderRadius: 2,
            bgcolor: '#fafafa'
          }}
        >
          <TimelineIcon sx={{ fontSize: '3rem', color: 'text.disabled', mb: 2 }} />
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ fontWeight: 600, mb: 1 }}
          >
            No Events Captured Yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Events will appear here in real-time. Trigger a login or token acquisition to see MSAL activity.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {filteredGroups.map(([cid, items]) => (
            <Card key={cid} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {/* Group Header */}
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: '#f5f5f5', 
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TimelineIcon color="action" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Correlation ID
                  </Typography>
                  <Chip 
                    label={cid.length > 20 ? `${cid.substring(0, 18)}...` : cid} 
                    size="small" 
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                  />
                </Box>
                <Chip 
                  label={`${items.length} event${items.length > 1 ? 's' : ''}`} 
                  size="small" 
                  color="primary"
                />
              </Box>

              {/* Events */}
              <CardContent sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {items.map((it, idx) => (
                    <Paper 
                      key={idx} 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2, 
                        bgcolor: it.error ? '#fff5f5' : 'white', 
                        borderColor: it.error ? 'error.light' : 'divider',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 1
                        }
                      }}
                    >
                      <Grid container spacing={2} alignItems="flex-start">
                        {/* Time + Event Type */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
                          >
                            Timestamp
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: "'Consolas', monospace", 
                              fontSize: '0.75rem',
                              mb: 1
                            }}
                          >
                            {new Date(it.ts).toLocaleTimeString()}.{new Date(it.ts).getMilliseconds().toString().padStart(3, '0')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip 
                              label={String(it.eventType)} 
                              size="small" 
                              color={it.error ? 'error' : 'primary'}
                              sx={{ fontWeight: 600 }}
                            />
                            {it.source && (
                              <Chip 
                                label={it.source === 'test' ? '🧪 Test' : 'App'} 
                                size="small" 
                                variant="outlined"
                                color={it.source === 'test' ? 'secondary' : 'default'}
                                sx={{ fontWeight: 500, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </Grid>

                        {/* Interaction Type */}
                        <Grid item xs={12} sm={6} md={2}>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
                          >
                            Interaction
                          </Typography>
                          <Chip 
                            label={it.interactionType ? String(it.interactionType) : 'None'} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </Grid>

                        {/* Message + Actions */}
                        <Grid item xs={12} md={7}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            {it.error && <ErrorOutlineIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />}
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
                              >
                                {it.error ? 'Error Details' : 'Event Details'}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: "'Consolas', monospace", 
                                  fontSize: '0.8rem',
                                  whiteSpace: 'pre-wrap', 
                                  wordBreak: 'break-word'
                                }}
                              >
                                {it.error || it.message || String(it.eventType)}
                              </Typography>
                            </Box>
                            <Tooltip title="Copy payload JSON">
                              <IconButton 
                                size="small" 
                                onClick={() => copy(JSON.stringify(redact(it.payload, enablePII), null, 2))}
                                sx={{ mt: 2 }}
                              >
                                {copyOk ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {/* Payload Accordion */}
                          {it.payload && (
                            <Accordion 
                              sx={{ 
                                mt: 2,
                                '&:before': { display: 'none' },
                                borderRadius: '8px !important',
                                border: '1px solid #e0e0e0'
                              }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                  📦 Full Payload
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ bgcolor: '#fafafa', pt: 2 }}>
                                <Box 
                                  sx={{ 
                                    fontFamily: "'Consolas', 'Monaco', monospace", 
                                    fontSize: '0.72rem', 
                                    whiteSpace: 'pre-wrap', 
                                    wordBreak: 'break-word', 
                                    bgcolor: '#fff', 
                                    p: 2, 
                                    borderRadius: 1, 
                                    border: '1px solid #e0e0e0',
                                    maxHeight: 400,
                                    overflowY: 'auto'
                                  }}
                                >
                                  {JSON.stringify(redact(it.payload, enablePII), null, 2)}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* About Section - Expandable */}
      <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                About MSAL Trace Viewer
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2.5}>
              <Typography variant="body2" color="text.secondary">
                The MSAL Trace Viewer provides real-time visibility into all authentication events generated by the 
                MSAL.js library. This tool captures low-level browser events during login, token acquisition, and logout 
                operations, making it invaluable for debugging authentication issues and understanding MSAL's internal behavior.
              </Typography>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  📊 What You Can See:
                </Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                      Event Lifecycle & Correlation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Events are automatically grouped by correlation ID, showing you the complete flow of a single authentication 
                      operation from start to finish. Each group represents one logical authentication action (login, token acquisition, etc.).
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                      Interaction Types
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      See which authentication method triggered each event: <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>popup</code>, 
                      {' '}<code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>redirect</code>, 
                      {' '}<code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>silent</code> (iframe), 
                      or <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>none</code> (initialization events).
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                      Event Types & Timing
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Track critical events like <strong>msal:acquireTokenStart</strong>, <strong>msal:loginSuccess</strong>, 
                      {' '}<strong>msal:acquireTokenSuccess</strong>, <strong>msal:acquireTokenFailure</strong>, 
                      {' '}<strong>msal:popupOpened</strong>, and many more. Timestamps help identify performance bottlenecks.
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                      Rich Event Payloads
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Each event includes detailed payload data such as:
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5, ml: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        • <strong>Request details:</strong> Scopes, authority, client ID, prompt behavior
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • <strong>Response data:</strong> Token expiration times, account info, granted scopes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • <strong>Cache state:</strong> What's stored in localStorage, sessionStorage, and browser cache
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • <strong>Browser context:</strong> Window references, DOM state during popup operations
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • <strong>Error details:</strong> Complete error messages, error codes, stack traces
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                      Non-Serializable Objects
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Some events contain references to browser objects (Window, DOM elements) that cannot be fully serialized. 
                      These are labeled as <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>[Window]</code> 
                      {' '}or <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>[HTMLElement]</code>. 
                      The tool will still extract all serializable properties and show object keys for debugging context.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  🎯 Common Use Cases:
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Chip label="Debugging" size="small" color="error" sx={{ mr: 1, minWidth: 100, mt: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Identify why authentication is failing by examining error events, request parameters, and error codes in the payload
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Chip label="Performance" size="small" color="warning" sx={{ mr: 1, minWidth: 100, mt: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Measure time between acquireTokenStart and acquireTokenSuccess to identify slow token acquisitions
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Chip label="Testing" size="small" color="success" sx={{ mr: 1, minWidth: 100, mt: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Verify that silent token refresh works correctly, or test popup vs. redirect flows side-by-side
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Chip label="Learning" size="small" color="info" sx={{ mr: 1, minWidth: 100, mt: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Understand MSAL's internal flow: what events fire in what order, what data is cached, how correlation IDs work
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  🔧 Features:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={0.75}>
                      <Typography variant="body2" color="text.secondary">
                        ✅ <strong>Real-time capture</strong> - Events appear instantly as they happen
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ✅ <strong>Correlation grouping</strong> - Related events grouped automatically
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ✅ <strong>PII redaction</strong> - Tokens and credentials masked by default
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ✅ <strong>Filtering options</strong> - Show only errors or specific interaction types
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={0.75}>
                      <Typography variant="body2" color="text.secondary">
                        ✅ <strong>JSON export</strong> - Download full trace for offline analysis
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ✅ <strong>Copy payloads</strong> - One-click copy of any event payload
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ✅ <strong>Test actions</strong> - Built-in buttons to trigger MSAL operations
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ✅ <strong>Clear history</strong> - Start fresh without page reload
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  💡 Pro Tips:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    • Enable PII to see full token contents and user identifiers during development (never in production)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Use "Errors Only" filter to quickly spot authentication failures in a long trace
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Export traces to JSON for sharing with team members or attaching to bug reports
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Compare correlation groups between working and failing scenarios to pinpoint differences
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Check the payload of <strong>msal:acquireTokenFailure</strong> events for detailed error diagnostics
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ bgcolor: '#f0f7ff', p: 2, borderRadius: 1, border: '1px solid #90caf9' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>
                  🎓 Educational Value:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This tool reveals how MSAL.js works under the hood. By observing the event sequence, you'll learn:
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1, ml: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    • How MSAL manages token cache and when it decides to refresh tokens silently
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • The difference between popup, redirect, and silent authentication patterns
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • What data MSAL stores in browser storage (localStorage/sessionStorage)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • How correlation IDs tie together multiple network requests in a single flow
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • When and why MSAL emits specific events during the authentication lifecycle
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 1, border: '1px solid #ffb74d' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.dark', mb: 1 }}>
                  ⚠️ Privacy Notice:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  By default, sensitive data (tokens, secrets, usernames) is redacted from payloads. Enable "Show PII" only 
                  in development environments. Never enable PII in production or share traces containing PII publicly.
                </Typography>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Container>
  );
};

export default MsalTraceViewer;
